import { useIsProjectTechnicalWriter } from "@/auth/permissions";
import { useFullReleaseStrict } from "@/common/hooks_useFullRelease";
import { TriggerIntroduction, TriggerSectionTitle } from "@/features/triggers/components/TriggerGuidance";
import Button from "@imminently/decisively-ui/Button";
import { FieldWatch, useFormContext } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import Icon from "@imminently/decisively-ui/Icon";
import SelectField from "@imminently/decisively-ui/SelectField";
import TextField from "@imminently/decisively-ui/TextField";
import { Typography } from "@mui/material";
import { get } from "lodash";
import { useState } from "react";
import { TriggerScheduling } from "./TriggerScheduling";
import { getCombinedConnections, isDcsvlyModule } from "./decisivelyModules";

export const AttributeIntegrationForm = ({
  scope,
  showTitle = true,
  showScheduling = true,
  mappingType = "delta",
}: {
  scope: string;
  showTitle?: boolean;
  showScheduling?: boolean;
  mappingType?: "delta" | "assignment";
}) => {
  const form = useFormContext<any>();
  const release = useFullReleaseStrict();
  const canEdit = useIsProjectTechnicalWriter();
  const [showMappingHelp, setShowMappingHelp] = useState(false);

  const connections = getCombinedConnections(release.connections).map((it) => ({ label: it.key, value: it.value }));
  const requestMapping = release.mapping?.map((it) => ({ label: it.name, value: it.id }));
  const responseMapping = release.mapping?.map((it) => ({ label: it.name, value: it.id }));
  const deltaMapping = release.mapping
    ?.filter((it) => it.type === "delta")
    .map((it) => ({ label: it.name, value: it.id }));

  const contentTypeOptions = [
    { label: "JSON", value: "application/json" },
    { label: "Form URL Encoded", value: "application/x-www-form-urlencoded" },
  ];

  const methodOptions = [
    { label: "GET", value: "get" },
    { label: "POST", value: "post" },
    { label: "PUT", value: "put" },
    { label: "PATCH", value: "patch" },
    { label: "DELETE", value: "delete" },
  ];

  const scopedValue = (name: string) => `${scope}.${name}`;

  return (
    <FieldWatch names={[scopedValue("connection")]}>
      {(values: any) => {
        const selectedConnValue = get(values, scopedValue("connection"));
        const selectedConnection = release.connections?.find((it) => it.id === selectedConnValue);
        const isDecisivelyModule = isDcsvlyModule(selectedConnValue);
        return (
          <>
            {showTitle && (
              <FormPanel>
                <TriggerIntroduction type="integration" />
                <TriggerSectionTitle>Details</TriggerSectionTitle>
                <FormPanel>
                  <TextField
                    label="Title"
                    required
                    name={scopedValue("title")}
                    disabled={!canEdit}
                    fullWidth
                  />
                </FormPanel>
              </FormPanel>
            )}

            <FormPanel>
              <TriggerSectionTitle>Connection</TriggerSectionTitle>
              <FormPanel>
                <SelectField
                  label="Connection"
                  required
                  name={scopedValue("connection")}
                  options={connections || []}
                  disabled={!canEdit}
                  fullWidth
                />

                {/* Show Base URL from selected connection (only for regular connections, not Decisively modules) */}
                {selectedConnection?.url && !isDecisivelyModule && (
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                  >
                    Base URL: <strong>{selectedConnection.url}</strong>
                  </Typography>
                )}

                <TextField
                  required
                  label="Path (relative to Base URL)"
                  name={scopedValue("path")}
                  disabled={!canEdit}
                  fullWidth
                />
              </FormPanel>
            </FormPanel>

            <FormPanel>
              <TriggerSectionTitle>API Settings</TriggerSectionTitle>
              <FormPanel>
                <SelectField
                  label="HTTP Method"
                  required
                  name={scopedValue("restMethod")}
                  options={methodOptions}
                  disabled={!canEdit}
                  fullWidth
                />
                <SelectField
                  label="Content Type"
                  required
                  name={scopedValue("contentType")}
                  options={contentTypeOptions}
                  disabled={!canEdit}
                  fullWidth
                />
                <TextField
                  label="Query Parameters"
                  name={scopedValue("query")}
                  disabled={!canEdit}
                  fullWidth
                />
              </FormPanel>
            </FormPanel>

            <FormPanel>
              <div className="flex items-center gap-1">
                <TriggerSectionTitle>Mapping</TriggerSectionTitle>
                <Button
                  icon
                  size="sm"
                  label="Show mapping help"
                  onClick={() => setShowMappingHelp(!showMappingHelp)}
                >
                  <Icon name="help" />
                </Button>
              </div>
              {showMappingHelp && (
                <div className="mb-4 flex flex-col items-start gap-6 md:flex-row">
                  <div
                    className="w-full md:w-auto md:max-w-[500px] md:flex-none"
                    style={{ flex: "1 1 auto" }}
                  >
                    <img
                      src="/help/communication_mapping.png"
                      alt="Mapping help"
                      style={{ maxWidth: "100%", height: "auto", display: "block" }}
                    />
                  </div>
                  <div className="flex-1">
                    <Typography
                      variant="h6"
                      gutterBottom
                    >
                      Mapping Flow
                    </Typography>
                    <Typography
                      variant="body2"
                      component="div"
                      style={{ lineHeight: 1.8 }}
                    >
                      <p className="mb-2">
                        <strong>1. Request Preparation:</strong> The request for the external service is prepared and
                        values in the query are transformed using the values in the session. If the external service
                        needs a JSON body, then you set the 'request' mapping to convert data from Decisively into the
                        format the external system expects.
                      </p>
                      <p className="mb-2">
                        <strong>2. Response Processing:</strong> When the response from the external service returns, it
                        is mapped using two mapping files:
                      </p>
                      <ul className="mb-2 pl-3">
                        <li className="mb-1">
                          <strong>Assignment Mapping:</strong> Takes the response and attempts to extract the contact
                          IDs of the assignees. It will use this to call the contact service and get appropriate
                          communication fields (like the email address).
                        </li>
                        <li>
                          <strong>Response Mapping:</strong> Optionally takes the response, converts into Decisively
                          format and then updates the session. You can use this to record information from the external
                          system back into Decisively.
                        </li>
                      </ul>
                    </Typography>
                  </div>
                </div>
              )}
              <FormPanel>
                {mappingType === "delta" && (
                  <SelectField
                    label="Delta Mapping"
                    helperText="Delta Mapping is used to calculate the difference between the current and previous version of the data"
                    name={scopedValue("deltaMapping")}
                    options={deltaMapping || []}
                    disabled={!canEdit}
                    fullWidth
                  />
                )}
                <SelectField
                  label="Transform Decisively To External data (request)"
                  name={scopedValue("requestMapping")}
                  options={requestMapping || []}
                  disabled={!canEdit}
                  fullWidth
                />
                {mappingType === "assignment" && (
                  <SelectField
                    label="Assignment Mapping"
                    helperText="Maps the integration response to extract contact IDs"
                    name={scopedValue("assignmentMapping")}
                    options={responseMapping || []}
                    disabled={!canEdit}
                    fullWidth
                  />
                )}
                <SelectField
                  label="Transform External data To Decisively (response)"
                  helperText={
                    mappingType === "assignment"
                      ? "Optional: updates the session with assignees for display/audit"
                      : undefined
                  }
                  name={scopedValue("responseMapping")}
                  options={responseMapping || []}
                  disabled={!canEdit}
                  fullWidth
                />
              </FormPanel>
            </FormPanel>

            <FormPanel>
              <TriggerSectionTitle>Retry Settings</TriggerSectionTitle>
              <FormPanel>
                <TextField
                  type="number"
                  label="Retry Attempts"
                  name={scopedValue("retryAttempts")}
                  disabled={!canEdit}
                  fullWidth
                />
              </FormPanel>
            </FormPanel>

            {showScheduling && (
              <FormPanel>
                <TriggerSectionTitle>Scheduling</TriggerSectionTitle>
                <FormPanel>
                  <TriggerScheduling
                    scope={scope}
                    disabled={!canEdit}
                  />
                </FormPanel>
              </FormPanel>
            )}
          </>
        );
      }}
    </FieldWatch>
  );
};
/*
{
  "url": "string",
  "method": "GET|POST|PUT|PATCH|DELETE",
  "contentType": "application/json|application/x-www-form-urlencoded",
  "authType": "None|Basic|API Key|Oauth 2.0|JWT|Bearer",
  // Required for Basic
  "username": "string",
  "password": "string",
  // Required for API Key or Bearer
  "apiKey": "string",
  // Required for Oauth 2.0
  "oauth": {
    "clientId": "string",
    "clientSecret": "string",
    "tokenUrl": "string",
    "scope": "string"
  },
  // Required for JWT
  "jwt": "string",

  "query": "string",
  "requestMapping": "linked mapping id",
  "responseMapping": "linked mapping id",
  "retryAttempts": "number",
  "notificationEmail": "email",
  "tlsVersion": "1.2|1.3",
  "verifyCerts": "boolean"
}
  */
